import React, { useState, useEffect, useMemo } from 'react';
import Modal from '@/components/ui/modal';
import { VoyageItem } from '@/types/voyage';
import { getDetailVesselReport } from '@/api/queries/vessels';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import ShipLoading from '@/components/ui/loader/ShipLoading';
import LineChart from '@/components/ui/charts/LineChart';
import DatatableWithTheme from '@/components/ui/datatableWIthTheme';
import {
  TableProperties,
  Gauge,
  Calendar,
  BarChart3,
  Cloud,
  Compass,
  Thermometer,
  Wind,
  Droplets,
  Eye,
  MapPin,
  Navigation,
  Waves,
  Anchor,
  FileText,
  FileSpreadsheet,
  Clock,
  ArrowRight,
  Route,
  X,
  Map,
} from 'lucide-react';
import SimpleRouteMap from './SimpleRouteMap';
import clsx from 'clsx';
import { cn } from '@/utils';

interface VoyageDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  voyage: VoyageItem | null;
  vesselId: string;
  vesselName: string;
  onExport: (exportType: 'pdf' | 'xlsx', voyage: VoyageItem) => Promise<void>;
  exporting: boolean;
}

const TABS = [
  { id: 'timeline', label: 'Timeline', icon: TableProperties },
  { id: 'summary', label: 'Summary', icon: Gauge },
  { id: 'daily', label: 'Daily', icon: Calendar },
  { id: 'chart', label: 'Chart', icon: BarChart3 },
  { id: 'map', label: 'Map Route', icon: Map },
] as const;

class ModalErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  componentDidCatch(error: Error, errorInfo: any) {
    console.error('[VoyageDetailModal] Error caught in ModalErrorBoundary:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-center bg-white dark:bg-gray-900 rounded-2xl border border-red-200 dark:border-red-900/50">
          <div className="h-12 w-12 rounded-full bg-red-50 dark:bg-red-950/40 text-red-500 mx-auto flex items-center justify-center mb-3">
            <X className="h-6 w-6" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Gagal Memuat Detail Pelayaran
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
            {this.state.error?.message || 'Terjadi kesalahan saat memproses data telemetri.'}
          </p>
          <button
            type="button"
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-4 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white text-xs font-semibold rounded-lg shadow-sm"
          >
            Coba Lagi
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function VoyageDetailModal({
  isOpen,
  onClose,
  voyage,
  vesselId,
  vesselName,
  onExport,
  exporting,
}: VoyageDetailModalProps) {
  const [activeTab, setActiveTab] = useState<string>('timeline');

  useEffect(() => {
    setActiveTab('timeline');
  }, [voyage?.voyage_key]);

  const queryVesselId = voyage?.vessel_id || vesselId || '';

  // Format dates for history report query safely
  const fromDate =
    voyage?.departure_time && moment(voyage.departure_time).isValid()
      ? moment(voyage.departure_time).format('YYYY-MM-DD HH:mm:ss')
      : '';
  const toDate =
    voyage?.arrival_time && moment(voyage.arrival_time).isValid()
      ? moment(voyage.arrival_time).format('YYYY-MM-DD HH:mm:ss')
      : moment().format('YYYY-MM-DD HH:mm:ss');

  // Fetch telemetry history report for this voyage period
  const { data: detailData, isLoading, isFetching } = useQuery({
    queryKey: ['DetailVesselsReportVoyage', queryVesselId, fromDate, toDate],
    queryFn: () => getDetailVesselReport(queryVesselId, fromDate, toDate, true, 'history'),
    enabled: isOpen && !!queryVesselId && !!fromDate && !!toDate,
    staleTime: 1000 * 60 * 5,
  });

  const timelineRows = detailData?.timeline || [];
  const summaryRows = detailData?.summary || [];
  const dailyRows = detailData?.daily || [];

  const routePoints: [number, number][] = useMemo(() => {
    if (voyage?.route && Array.isArray(voyage.route) && voyage.route.length >= 2) {
      return voyage.route;
    }
    return timelineRows
      .map((r: any) => [parseFloat(r?.latitude), parseFloat(r?.longitude)] as [number, number])
      .filter(([lat, lon]) => !isNaN(lat) && !isNaN(lon) && (lat !== 0 || lon !== 0));
  }, [voyage?.route, timelineRows]);

  // ALL HOOKS HAVE BEEN EXECUTED ABOVE — SAFE EARLY RETURN HERE
  if (!isOpen || !voyage) return null;

  const status = voyage.status || 'completed';
  const statusConfig: Record<string, { label: string; bg: string; text: string; dot: string }> = {
    completed: {
      label: 'Completed',
      bg: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800/50',
      text: 'text-emerald-700 dark:text-emerald-400',
      dot: 'bg-emerald-500',
    },
    ongoing: {
      label: 'Ongoing',
      bg: 'bg-sky-50 dark:bg-sky-950/40 border-sky-200 dark:border-sky-800/50',
      text: 'text-sky-700 dark:text-sky-400',
      dot: 'bg-sky-500 animate-pulse',
    },
    maneuver: {
      label: 'Maneuver',
      bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800/50',
      text: 'text-amber-700 dark:text-amber-400',
      dot: 'bg-amber-500',
    },
    stopped: {
      label: 'Stopped',
      bg: 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700',
      text: 'text-gray-700 dark:text-gray-300',
      dot: 'bg-gray-400',
    },
  };
  const currentStatus = statusConfig[status.toLowerCase()] || statusConfig.completed;

  return (
    <Modal modal={isOpen} setModal={onClose} className="max-w-6xl w-full max-h-[92vh] overflow-y-auto p-4 sm:p-6">
      <ModalErrorBoundary>
        <div className="space-y-5">
          {/* ── HEADER BANNER ── */}
          <div className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800 bg-gradient-to-r from-brand-600 via-brand-500 to-blue-600 text-white shadow-sm p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3 sm:gap-4 min-w-0">
                <div className="h-10 w-10 sm:h-12 sm:w-12 shrink-0 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/25 mt-0.5 sm:mt-0">
                  <Route className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] sm:text-xs uppercase tracking-[0.16em] text-white/80 font-bold">
                      {vesselName}
                    </span>
                    <span
                      className={clsx(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border backdrop-blur',
                        currentStatus.bg,
                        currentStatus.text,
                      )}
                    >
                      <span className={clsx('h-1.5 w-1.5 rounded-full', currentStatus.dot)} />
                      {currentStatus.label}
                    </span>
                  </div>
                  <h2 className="text-lg sm:text-2xl font-bold tracking-tight mt-1 flex items-center gap-2 flex-wrap">
                    <span>{voyage.origin_name || 'Origin'}</span>
                    <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 text-white/70 shrink-0" />
                    <span>{voyage.destination_name || 'Destination'}</span>
                  </h2>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-white/85 flex-wrap">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5 text-white/70" />
                      {voyage.departure_time
                        ? moment(voyage.departure_time).format('DD MMM YYYY, HH:mm')
                        : '-'}
                    </span>
                    <span>→</span>
                    <span>
                      {voyage.arrival_time
                        ? moment(voyage.arrival_time).format('DD MMM YYYY, HH:mm')
                        : 'Sekarang (Ongoing)'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Export Actions */}
              <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
                <button
                  type="button"
                  disabled={exporting}
                  onClick={() => onExport('pdf', voyage)}
                  className="flex-1 sm:flex-initial h-9 px-3.5 rounded-xl bg-white/15 hover:bg-white/25 active:scale-95 text-white text-xs font-semibold inline-flex items-center justify-center gap-1.5 backdrop-blur ring-1 ring-white/25 transition disabled:opacity-50"
                  title="Ekspor laporan riwayat voyage ini ke PDF"
                >
                  <FileText className="h-4 w-4" />
                  <span>Export PDF</span>
                </button>
                <button
                  type="button"
                  disabled={exporting}
                  onClick={() => onExport('xlsx', voyage)}
                  className="flex-1 sm:flex-initial h-9 px-3.5 rounded-xl bg-white text-brand-600 hover:bg-white/90 active:scale-95 text-xs font-semibold inline-flex items-center justify-center gap-1.5 shadow-sm transition disabled:opacity-50"
                  title="Ekspor laporan riwayat voyage ini ke Excel"
                >
                  <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                  <span>Export Excel</span>
                </button>
              </div>
            </div>
          </div>

          {/* ── METRICS STRIP ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-3 bg-gray-50/70 dark:bg-gray-800/40">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold flex items-center gap-1">
                <Navigation className="h-3 w-3 text-blue-500" /> Jarak Pelayaran
              </div>
              <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                {(Number(voyage.distance_km || 0) * 0.539957).toFixed(1)}{' '}
                <span className="text-xs font-normal text-gray-500">NM</span>
              </div>
              <div className="text-[10px] text-gray-400">({Number(voyage.distance_km || 0).toFixed(1)} km)</div>
            </div>

            <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-3 bg-gray-50/70 dark:bg-gray-800/40">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold flex items-center gap-1">
                <Clock className="h-3 w-3 text-emerald-500" /> Durasi Layar
              </div>
              <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                {Number(voyage.travel_time_hours || 0).toFixed(1)}{' '}
                <span className="text-xs font-normal text-gray-500">Jam</span>
              </div>
              <div className="text-[10px] text-gray-400">
                ({(Number(voyage.travel_time_hours || 0) / 24).toFixed(1)} hari)
              </div>
            </div>

            <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-3 bg-gray-50/70 dark:bg-gray-800/40">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold flex items-center gap-1">
                <Gauge className="h-3 w-3 text-amber-500" /> Rata-Rata Speed
              </div>
              <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                {Number(voyage.avg_speed_knots || 0).toFixed(1)}{' '}
                <span className="text-xs font-normal text-gray-500">knots</span>
              </div>
              <div className="text-[10px] text-gray-400">Kecepatan rerata</div>
            </div>

            <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-3 bg-gray-50/70 dark:bg-gray-800/40">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold flex items-center gap-1">
                <TableProperties className="h-3 w-3 text-purple-500" /> Titik Telemetri
              </div>
              <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                {timelineRows.length}{' '}
                <span className="text-xs font-normal text-gray-500">records</span>
              </div>
              <div className="text-[10px] text-gray-400">Riwayat GPS terdata</div>
            </div>
          </div>

          {/* ── TABS NAVIGATION ── */}
          <div className="flex items-center gap-1.5 p-1 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 w-full sm:w-auto">
            {TABS.map((t) => {
              const active = activeTab === t.id;
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={clsx(
                    'flex-1 sm:flex-initial h-9 px-3.5 rounded-lg inline-flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold transition-all',
                    active
                      ? 'bg-white dark:bg-gray-900 text-brand-600 dark:text-brand-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white',
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span>{t.label}</span>
                </button>
              );
            })}
          </div>

          {/* ── TAB CONTENT ── */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-4 min-h-[300px]">
            {isLoading || isFetching ? (
              <div className="py-12">
                <ShipLoading label="Memuat riwayat telemetri, cuaca & daily pelayaran..." />
              </div>
            ) : timelineRows.length === 0 && routePoints.length === 0 ? (
              <div className="py-12 text-center text-gray-400 dark:text-gray-500">
                <TableProperties className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm font-medium">Tidak ada rekaman telemetri detail pada rentang tanggal pelayaran ini.</p>
                <p className="text-xs mt-1 text-gray-400">
                  Waktu: {fromDate} s/d {toDate}
                </p>
              </div>
            ) : (
              <>
                {activeTab === 'timeline' && <ModalTimelineTable rows={timelineRows} />}
                {activeTab === 'summary' && (
                  <ModalSummaryView
                    data={{
                      timeline: timelineRows,
                      summary: summaryRows,
                      daily: dailyRows,
                    }}
                  />
                )}
                {activeTab === 'daily' && <ModalDailyTable rows={dailyRows} />}
                {activeTab === 'chart' && <ModalChartView rows={timelineRows} />}
                {activeTab === 'map' && (
                  <SimpleRouteMap
                    routePoints={routePoints}
                    vesselName={vesselName}
                    startLabel={voyage.origin_name || 'Origin'}
                    endLabel={voyage.destination_name || 'Destination'}
                    startDate={
                      voyage.departure_time
                        ? moment(voyage.departure_time).format('DD MMM YYYY, HH:mm')
                        : '-'
                    }
                    endDate={
                      voyage.arrival_time
                        ? moment(voyage.arrival_time).format('DD MMM YYYY, HH:mm')
                        : 'Sekarang'
                    }
                    height="520px"
                  />
                )}
              </>
            )}
          </div>
        </div>
      </ModalErrorBoundary>
    </Modal>
  );
}

// ── SUB-COMPONENTS ──

function fmtCoord(v: string | number) {
  if (!v) return '0.00000';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return isNaN(n) ? '0.00000' : n.toFixed(5);
}

function SpeedBar({ value }: { value: number }) {
  const safeVal = isNaN(value) ? 0 : value;
  const pct = Math.min(100, (safeVal / 15) * 100);
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="h-1.5 flex-1 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
        <div className="h-full rounded-full bg-brand-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-medium tabular-nums w-14 text-right text-gray-900 dark:text-white">
        {safeVal.toFixed(2)} kn
      </span>
    </div>
  );
}

function ModalTimelineTable({ rows }: { rows: any[] }) {
  const safeRows = rows || [];
  const columns = [
    {
      name: 'Date',
      selector: (row: any) => row.receive_date,
      sortable: true,
      cell: (row: any) => (
        <div className="whitespace-nowrap py-2.5">
          <div className="font-medium text-gray-900 dark:text-white">{row.receive_date?.slice(0, 10)}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{row.receive_date?.slice(11)}</div>
        </div>
      ),
      width: '120px',
    },
    {
      name: 'Position',
      selector: (row: any) => row.latitude,
      sortable: true,
      cell: (row: any) => (
        <div className="tabular-nums whitespace-nowrap text-xs">
          <div className="text-gray-900 dark:text-white">{fmtCoord(row.latitude)}</div>
          <div className="text-gray-500 dark:text-gray-400">{fmtCoord(row.longitude)}</div>
        </div>
      ),
      width: '130px',
    },
    {
      name: 'Heading',
      selector: (row: any) => parseFloat(row.heading || '0'),
      sortable: true,
      cell: (row: any) => {
        const heading = parseFloat(row.heading || '0') || 0;
        return (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white text-xs">
            <Compass
              className="h-3.5 w-3.5 text-brand-500"
              style={{ transform: `rotate(${heading}deg)` }}
            />{' '}
            {heading.toFixed(0)}°
          </span>
        );
      },
      width: '100px',
    },
    {
      name: 'Speed',
      selector: (row: any) => parseFloat(row.speed || '0'),
      sortable: true,
      cell: (row: any) => <SpeedBar value={parseFloat(row.speed || '0')} />,
      width: '150px',
    },
    {
      name: 'Distance',
      selector: (row: any) => parseFloat(row.distance || '0'),
      sortable: true,
      cell: (row: any) => (
        <div className="tabular-nums text-xs text-gray-900 dark:text-white">
          {(parseFloat(row.distance || '0') || 0).toFixed(2)}{' '}
          <span className="text-[11px] text-gray-500 dark:text-gray-400">nm</span>
        </div>
      ),
      width: '100px',
    },
    {
      name: 'Weather',
      selector: (row: any) => row.vessel_history_meteorology?.summary || '',
      sortable: true,
      cell: (row: any) => {
        const m = row.vessel_history_meteorology;
        return m ? (
          <div className="flex items-center gap-1.5 text-xs">
            <Cloud className="h-3.5 w-3.5 text-blue-500 shrink-0" />
            <span className="capitalize text-gray-900 dark:text-gray-200">{m.summary}</span>
            <span className="text-gray-500 dark:text-gray-400">· {(parseFloat(m.clouds || '0') || 0).toFixed(0)}%</span>
          </div>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
        );
      },
      width: '140px',
    },
    {
      name: 'Temp',
      selector: (row: any) => parseFloat(row.vessel_history_meteorology?.temp || '0'),
      sortable: true,
      cell: (row: any) => {
        const m = row.vessel_history_meteorology;
        return m ? (
          <span className="inline-flex items-center gap-1 text-xs text-gray-900 dark:text-white tabular-nums">
            <Thermometer className="h-3.5 w-3.5 text-orange-500" />
            {(parseFloat(m.temp || '0') || 0).toFixed(1)}°C
          </span>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
        );
      },
      width: '95px',
    },
    {
      name: 'Wind & Env',
      selector: (row: any) => parseFloat(row.vessel_history_meteorology?.wind_speed || '0'),
      sortable: true,
      cell: (row: any) => {
        const m = row.vessel_history_meteorology;
        return m ? (
          <div className="text-[11px] tabular-nums">
            <div className="flex items-center gap-1 text-gray-900 dark:text-gray-200">
              <Wind className="h-3 w-3 text-blue-500" /> {(parseFloat(m.wind_speed || '0') || 0).toFixed(1)} m/s
            </div>
            {m.humidity && (
              <div className="text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <Droplets className="h-2.5 w-2.5" /> {m.humidity}%
                {m.visibility && (
                  <>
                    <span>·</span>
                    <Eye className="h-2.5 w-2.5" /> {((parseFloat(m.visibility || '0') || 0) / 1000).toFixed(0)}km
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-600">—</span>
        );
      },
      width: '130px',
    },
    {
      name: 'Proximity',
      selector: (row: any) => row.proximity || row.proximity_name || '',
      sortable: true,
      cell: (row: any) => (
        <div className="flex items-start gap-1.5 text-gray-500 dark:text-gray-400 min-w-[180px] whitespace-normal py-1">
          <MapPin className="h-3.5 w-3.5 mt-0.5 text-brand-500/70 shrink-0" />
          <div className="text-xs">
            <div className="text-gray-900 dark:text-gray-200 leading-snug">
              {row.proximity || row.proximity_name || 'Open Sea'}
            </div>
            {row.proximity_distance != null && !isNaN(Number(row.proximity_distance)) && (
              <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                {Number(row.proximity_distance).toFixed(0)} m
                {row.proximity_direction ? ` · ${String(row.proximity_direction).trim()}` : ''}
              </div>
            )}
          </div>
        </div>
      ),
      minWidth: '180px',
    },
  ];

  return (
    <div className="overflow-x-auto [&_.rdt_TableCell]:!px-2 [&_.rdt_TableCol]:!px-2">
      <DatatableWithTheme
        columns={columns}
        data={safeRows}
        pagination
        paginationPerPage={10}
        dense
      />
    </div>
  );
}

function ModalSummaryView({ data }: { data: any }) {
  const summaryList = data?.summary || [];
  const s = summaryList[0] ?? { average_speed: '0', max_speed: '0', total_distance: '0' };
  const timelineList = data?.timeline || [];
  const dailyList = data?.daily || [];
  const totalPoints = timelineList.length;
  const moving = dailyList.reduce((a: number, d: any) => a + (Number(d?.countNotStopped) || 0), 0);
  const stops = dailyList.reduce(
    (a: number, d: any) => a + ((Number(d?.count) || 0) - (Number(d?.countNotStopped) || 0)),
    0,
  );
  const days = dailyList.length;

  const stats = [
    {
      label: 'Total Distance',
      value: (Number(s?.total_distance) || 0).toFixed(2),
      unit: 'nm',
      icon: Navigation,
      tone: 'primary',
    },
    {
      label: 'Avg Speed',
      value: (Number(s?.average_speed) || 0).toFixed(2),
      unit: 'kn',
      icon: Gauge,
      tone: 'info',
    },
    {
      label: 'Max Speed',
      value: (Number(s?.max_speed) || 0).toFixed(2),
      unit: 'kn',
      icon: Waves,
      tone: 'success',
    },
    { label: 'Stops / Idle', value: `${stops}`, unit: 'events', icon: Anchor, tone: 'warning' },
    { label: 'Records Points', value: `${totalPoints}`, unit: 'points', icon: TableProperties, tone: 'primary' },
    { label: 'Moving Samples', value: `${moving}`, unit: `/ ${totalPoints}`, icon: Compass, tone: 'info' },
    { label: 'Days Covered', value: `${days}`, unit: 'days', icon: Calendar, tone: 'success' },
    {
      label: 'Avg Samples / Day',
      value: `${Math.round(totalPoints / Math.max(1, days))}`,
      unit: 'pts',
      icon: BarChart3,
      tone: 'primary',
    },
  ] as const;

  const toneMap: Record<string, string> = {
    primary: 'bg-brand-50 text-brand-500 dark:bg-brand-500/10 dark:text-brand-400',
    info: 'bg-blue-50 text-blue-500 dark:bg-blue-500/10 dark:text-blue-400',
    success: 'bg-green-50 text-green-500 dark:bg-green-500/10 dark:text-green-400',
    warning: 'bg-orange-50 text-orange-500 dark:bg-orange-500/10 dark:text-orange-400',
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {stats.map((st) => (
        <div
          key={st.label}
          className="rounded-xl border border-gray-100 dark:border-gray-800 p-4 bg-white dark:bg-gray-900 shadow-xs hover:shadow-sm transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {st.label}
            </div>
            <div className={cn('h-8 w-8 rounded-lg flex items-center justify-center', toneMap[st.tone])}>
              <st.icon className="h-4 w-4" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <div className="text-2xl font-bold tabular-nums text-gray-900 dark:text-white">{st.value}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{st.unit}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ModalDailyTable({ rows }: { rows: any[] }) {
  const safeRows = rows || [];
  const maxDist = Math.max(...safeRows.map((r) => Number(r?.distance || 0)), 0.01);

  const columns = [
    {
      name: 'Day',
      selector: (row: any) => row.date || row.receive_date?.slice(0, 10) || '',
      sortable: true,
      cell: (row: any) => (
        <div className="font-medium whitespace-nowrap text-gray-900 dark:text-white py-2 text-xs">
          {row.date || row.receive_date?.slice(0, 10) || '-'}
        </div>
      ),
      width: '120px',
    },
    {
      name: 'Distance',
      selector: (row: any) => Number(row.distance || 0),
      sortable: true,
      cell: (row: any) => {
        const dist = Number(row.distance || 0);
        return (
          <div className="flex items-center gap-2 min-w-[140px] w-full">
            <div className="h-1.5 flex-1 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
              <div
                className="h-full rounded-full bg-brand-500"
                style={{ width: `${Math.min(100, (dist / maxDist) * 100)}%` }}
              />
            </div>
            <span className="text-xs font-medium w-16 text-right text-gray-900 dark:text-white">
              {dist.toFixed(2)} nm
            </span>
          </div>
        );
      },
    },
    {
      name: 'Top Speed',
      selector: (row: any) => Number(row.speed || 0),
      sortable: true,
      cell: (row: any) => (
        <div className="tabular-nums text-xs text-gray-900 dark:text-white">
          {Number(row.speed || 0).toFixed(2)} kn
        </div>
      ),
      width: '110px',
    },
    {
      name: 'Samples',
      selector: (row: any) => parseInt(row.count || '0', 10),
      sortable: true,
      cell: (row: any) => (
        <div className="tabular-nums text-xs text-gray-900 dark:text-white">{row.count}</div>
      ),
      width: '100px',
    },
    {
      name: 'Activity',
      selector: (row: any) => {
        const count = parseInt(row.count || '0', 10);
        const countNotStopped = parseInt(row.countNotStopped || '0', 10);
        return count ? (countNotStopped / count) * 100 : 0;
      },
      sortable: true,
      cell: (row: any) => {
        const count = parseInt(row.count || '0', 10);
        const countNotStopped = parseInt(row.countNotStopped || '0', 10);
        const movePct = count ? (countNotStopped / count) * 100 : 0;
        return (
          <span
            className={cn(
              'inline-flex px-2 py-0.5 rounded-full text-xs font-medium',
              movePct === 0
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                : movePct < 30
                  ? 'bg-orange-50 dark:bg-orange-500/10 text-orange-500 dark:text-orange-400'
                  : 'bg-green-50 dark:bg-green-500/10 text-green-500 dark:text-green-400',
            )}
          >
            {movePct === 0 ? 'Idle' : `${movePct.toFixed(0)}% moving`}
          </span>
        );
      },
      width: '130px',
    },
  ];

  return (
    <div className="overflow-x-auto [&_.rdt_TableCell]:!px-2 [&_.rdt_TableCol]:!px-2">
      <DatatableWithTheme
        columns={columns}
        data={safeRows}
        pagination
        paginationPerPage={10}
        dense
      />
    </div>
  );
}

function ModalChartView({ rows }: { rows: any[] }) {
  const safeRows = rows || [];
  const speeds = safeRows.map((r) => Number(r?.speed || 0));
  const max = speeds.length > 0 ? Math.max(...speeds, 1) : 1;
  const labels = safeRows.map((r) => r?.receive_date || '');

  const dataset = {
    labels: labels,
    datasets: [
      {
        label: 'Speed (Knots)',
        data: speeds,
        fill: true,
        backgroundColor: 'rgba(0, 150, 251, 0.1)',
        borderColor: '#0096FB',
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 4,
        borderWidth: 2,
      },
    ],
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-white">Speed over time</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Knots · {speeds.length} samples · peak {max.toFixed(2)} kn
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-gray-100 dark:border-gray-800 p-4 bg-white dark:bg-gray-900 shadow-xs">
        {speeds.length > 0 ? (
          <LineChart title="Voyage Speed History" datasets={dataset} />
        ) : (
          <div className="flex items-center justify-center h-[240px] text-gray-400 text-sm">
            No telemetry data available for chart
          </div>
        )}
      </div>
    </div>
  );
}
